import {Injectable, inject} from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { GenericConfig } from '@signature-it/ngx-generic/config';
import {Observable, of, Subscription} from 'rxjs';
import { defaultIfEmpty } from "rxjs/operators";
import { UtilityService } from "./utility.service";
import { EncodeSeoUriPipe, DecodeSeoUriPipe } from '@signature-it/ngx-catalogue/pipes';
import {GroupApiService} from './group.service';
import { CatalogueTypes } from '@signature-it/ngx-catalogue/enums';
import {SeoService_Generic} from '@signature-it/ngx-generic/services';
import {SharedDataService} from "./shared-data.service";

@Injectable({
    providedIn: 'root'
})
export class GroupResolver  {

    groupsTree: any;
    subscriptions: Subscription;
    sharedData: SharedDataService;

    constructor(private router: Router,
                private utilitySvc: UtilityService,
                private encodeSeoUri: EncodeSeoUriPipe,
                private decodeSeoUri: DecodeSeoUriPipe,
                protected groupSvc: GroupApiService,
                protected seoSvc: SeoService_Generic) {
        this.sharedData = inject(SharedDataService);
    }

    resolve(route: ActivatedRouteSnapshot): Observable <any> {
        this.sharedData.getCurrentGroup.next(null);
        this.subscriptions = new Subscription();
        const CATALOGUE_TYPE = route.params['catalogueType']; // defined in CatalogueUrlMatcher
        if (GenericConfig.disable_friendly_url && ('catalogueType' in route.params && !CATALOGUE_TYPE || !('catalogueType' in route.params) && route.url[0].path !== 'group')) {
            this.router.navigate(['/404']);
        }
        const groupId = parseInt(route.params.groupId);
        const groupName = route.params.leafGroup || route.params.groupName;
        const entityUri = route.params.entityUri;
        this.groupSvc.initGroupsTree(CATALOGUE_TYPE !== CatalogueTypes.GROUP ? CATALOGUE_TYPE : '');
        return new Observable(observer => {
            this.subscriptions.add(this.groupSvc.loadingGroupsTree$.subscribe(loading => {
                if (!loading) {
                    this.subscriptions.add(this.groupSvc.getGroupsTree$.subscribe(groupsTree => {
                        if (groupsTree) {
                            this.subscriptions.unsubscribe();
                            this.groupsTree = groupsTree;
                            if (groupId) {
                                observer.next(this.resolveByGroupId(groupId, route));
                            }
                            if (entityUri) {
                                observer.next(this.resolveByGroupName(entityUri, route, 'entityUri'));
                            }
                            if (groupName && !GenericConfig.disable_friendly_url) {
                                observer.next(this.resolveByGroupName(groupName, route));
                            }
                            if (!groupId && !groupName && !entityUri) {
                                this.router.navigate(['/404']);
                            }
                            observer.complete();
                        }
                    }));
                }
            }));
       });
    }

    resolveByGroupId(groupId, route) {
        const group = this.groupSvc.getGroupByAttr({id: groupId});
        if (!group) {
            this.router.navigate(['/404']);
            return of().pipe(defaultIfEmpty(true));
        }
        if (GenericConfig.disable_friendly_url) {
            if (group.isleaf && route.params.leafGroup && route.params.leafGroup != group.slug.replace(/\//g, '%2F')) {
                const redirect = group.getUrl(route.params.familyName);
                this.seoSvc.setPrerenderStatusCode('301');
                this.seoSvc.setPrerenderHeader(`Location: ${location.origin}${redirect}${location.search}`);
                this.router.navigate([redirect], {queryParams: route.queryParams});
                return;
            }
            return groupId;
        }
        const cmd = group.getUrl(route.params.familyName);
        this.router.navigate([ cmd ], {queryParams: route.queryParams});
        return groupId;
    }

    resolveByGroupName(groupName, route, routeParam = 'groupName') {
        const searchKey = routeParam == 'entityUri' ? 'entityUri' : 'slug';
        const group = this.groupSvc.getGroupByAttr({[searchKey] : this.decodeSeoUri.transform(groupName)}, true);
        if (!group) {
            this.router.navigate(['/404']);
            return of().pipe(defaultIfEmpty(true));
        }
        if (GenericConfig.disable_friendly_url) {
            return group.id;
        }
        if (group.isleaf ) {
            const parentGroupName = encodeURIComponent(this.encodeSeoUri.transform(group.parent_slug));
            const leafGroupName = encodeURIComponent(this.encodeSeoUri.transform(group.slug));
            if(route.params.groupName != parentGroupName){
                let redirect = `/${group.catalogueFriendlyUrl}/${parentGroupName}/${leafGroupName}`;
                redirect += route.params.familyName ? `/${route.params.familyName}` : '';
                this.seoSvc.setPrerenderStatusCode('301');
                this.seoSvc.setPrerenderHeader(`Location: ${location.origin}${redirect}${location.search}`);
                this.router.navigate([redirect], {queryParams: route.queryParams});
                return;
            }
        }
        console.log(`Group ${group.id}`);
        return group.id;
    }
}
